import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Alert, Col, Container, Row } from 'reactstrap';
import { getCountry, useAppContext, useTranslator } from '@front-activerse/common-components';
import PeriodInfo from '../../ShoppingList/Content/PeriodInfo/PeriodInfo';
import InstacartButton from '../../ShoppingList/Content/InstacartButton';
import FriscoButton from '../../ShoppingList/Content/FriscoButton';
import ShoppingTipsAlert from '../../ShoppingList/Alerts/ShoppingTipsAlert';
import ShoppingListContentPlaceholder from '../../ShoppingList/Placeholder/ShoppingListContentPlaceholder';
import TrackerShoppingListSearch from './TrackerShoppingListSearch';
import TrackerShoppingListItem from './TrackerShoppingListItem';
import TrackerShoppingListBought from './TrackerShoppingListBought';
import { defaultPeriod, toOrderCategories } from './helpers';
import type {
  MarkItemInput,
  MarkItemResult,
  Period,
  ShoppingListRange,
  TrackerShoppingList,
  TrackerShoppingListItem as Item,
} from './types';

const ANALYTICS_LOCATION = 'Tracker_shopping_list';
const INSTACART_COUNTRIES = ['Canada', 'United States of America'];

type OutletContext = {
  trackFirebaseEvent: (name: string, params: Record<string, unknown>) => void;
};

interface Props {
  shoppingList: TrackerShoppingList | null;
  loading: boolean;
  showRange: (range: ShoppingListRange) => void;
  markItem: (input: MarkItemInput) => Promise<MarkItemResult>;
  shoppingTipsArticleId: number;
}

const withoutKey = (record: Record<string, boolean>, key: string) => {
  const next = { ...record };
  delete next[key];
  return next;
};

const TrackerShoppingListContainer: React.FC<Props> = ({
  shoppingList,
  loading,
  showRange,
  markItem,
  shoppingTipsArticleId,
}) => {
  const { t } = useTranslator();
  const { trackFirebaseEvent } = useOutletContext<OutletContext>();
  const {
    consts: { INSTACART_FIREBASE_FUNCTION, INSTACART_URL, FRISCO },
  } = useAppContext();

  const [period, setPeriod] = useState<Period>(() => defaultPeriod(new Date()));
  // Bought state the user has tapped but the server has not confirmed yet.
  const [pendingBought, setPendingBought] = useState<Record<string, boolean>>({});
  const savingItemIds = useRef(new Set<string>());

  const startDate = shoppingList?.startDate;
  const endDate = shoppingList?.endDate;

  useEffect(() => {
    trackFirebaseEvent('screen_view', { firebase_screen: 'TrackerShoppingList' });
  }, [trackFirebaseEvent]);

  useEffect(() => {
    if (startDate && endDate) setPeriod({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  const handleSubmit = (nextPeriod: Period) => {
    trackFirebaseEvent('user_action', {
      action: 'tap-show_shopping_list',
      location: ANALYTICS_LOCATION,
    });
    setPendingBought({});
    showRange({ startDate: nextPeriod.start, endDate: nextPeriod.end });
  };

  const handleToggle = async (item: Item) => {
    // One request per row at a time: concurrent mark/unmark calls could land on the server
    // in either order and leave the row out of sync with it.
    if (!shoppingList || savingItemIds.current.has(item.id)) return;
    savingItemIds.current.add(item.id);

    const isBought = !(pendingBought[item.id] ?? item.isBought);
    setPendingBought((prev) => ({ ...prev, [item.id]: isBought }));
    trackFirebaseEvent('user_action', {
      action: isBought ? 'tap-mark_shopping_list_item' : 'tap-unmark_shopping_list_item',
      location: ANALYTICS_LOCATION,
    });

    // On success the cache already holds the new state; on failure dropping the
    // pending entry snaps the row back.
    await markItem({
      itemId: item.id,
      startDate: shoppingList.startDate,
      endDate: shoppingList.endDate,
      isBought,
    });
    savingItemIds.current.delete(item.id);
    setPendingBought((prev) => withoutKey(prev, item.id));
  };

  const renderList = () => {
    if (loading) return <ShoppingListContentPlaceholder />;

    if (!shoppingList) {
      return (
        <Alert color="danger" fade={false}>
          <p className="small">{t('tracker.shoppingList.error')}</p>
        </Alert>
      );
    }

    if (shoppingList.items.length === 0) {
      return (
        <Alert color="danger" fade={false}>
          <p className="small">{t('shoppingList.notification.noProducts')}</p>
        </Alert>
      );
    }

    const items = shoppingList.items.map((item) => ({
      ...item,
      isBought: pendingBought[item.id] ?? item.isBought,
    }));
    const notBought = items.filter((item) => !item.isBought);
    const bought = items.filter((item) => item.isBought);
    const orderCategories = toOrderCategories(items, t);
    const showInstacart =
      INSTACART_FIREBASE_FUNCTION && INSTACART_URL && INSTACART_COUNTRIES.includes(getCountry());

    return (
      <>
        {/* @ts-expect-error -- PeriodInfo declares a `t` prop but reads t from useTranslator */}
        <PeriodInfo start={shoppingList.startDate} end={shoppingList.endDate} />

        {showInstacart && (
          <InstacartButton
            categories={orderCategories}
            start={shoppingList.startDate}
            end={shoppingList.endDate}
          />
        )}
        {FRISCO && (
          <FriscoButton
            categories={orderCategories}
            start={shoppingList.startDate}
            end={shoppingList.endDate}
          />
        )}

        <ul className="ingredients">
          {notBought.map((item) => (
            <TrackerShoppingListItem
              key={item.id}
              item={item}
              isBought={false}
              onToggle={handleToggle}
            />
          ))}
        </ul>

        <TrackerShoppingListBought count={bought.length}>
          {bought.map((item) => (
            <TrackerShoppingListItem key={item.id} item={item} isBought onToggle={handleToggle} />
          ))}
        </TrackerShoppingListBought>

        <hr className="my-xl" />
        <ShoppingTipsAlert shoppingTipsArticleId={shoppingTipsArticleId} />
      </>
    );
  };

  return (
    <Container className="py-md-4">
      <Row>
        <Col xl={{ size: 10, offset: 1 }}>
          <div className="page-container">
            <TrackerShoppingListSearch
              period={period}
              onPeriodChange={setPeriod}
              onSubmit={handleSubmit}
            />
            <div className="shopping-list-container">{renderList()}</div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default TrackerShoppingListContainer;
